// @ts-nocheck
/**
 * Bidirectional converters between DraftJS raw format and TipTap HTML.
 *
 * Why this file still exists:
 * MV-9801 removes the Draft.js editor/viewer runtime from the UI, but the
 * backend can still contain historical notes, chat messages, and descriptions
 * stored as Draft.js raw JSON. We keep this converter only as a migration
 * compatibility bridge so old content can be normalized into TipTap-readable
 * content instead of breaking read-only/edit screens. It should not be used to
 * reintroduce Draft.js rendering or editing support.
 *
 * draftRawToTipTapHtml  — loads stored DraftJS JSON into TipTap editor
 * tiptapJsonToDraftRaw  — serialises TipTap editor state to the same DraftJS
 *                         JSON format the backend has always received.
 */

// ─── shared constants ────────────────────────────────────────────────────────

import { normalizeHyperlink } from '../../../../../utils/Utils';

const STYLE_TO_TAG = {
  BOLD: 'strong',
  ITALIC: 'em',
  UNDERLINE: 'u',
  CODE: 'code',
  STRIKETHROUGH: 's',
};

const TIPTAP_NODE_TO_ENTITY_TYPE = {
  userMention: 'mention',
  modelMention: 'model:mention',
  artifactMention: 'artifact:mention',
  documentMention: 'document:mention',
  attributeMention: 'attribute:mention',
};

const ENTITY_TYPE_TO_TIPTAP_NODE = {
  mention: 'userMention',
  'model:mention': 'modelMention',
  'artifact:mention': 'artifactMention',
  'document:mention': 'documentMention',
  'attribute:mention': 'attributeMention',
};

const DRAFT_BLOCK_TO_TAG = {
  unstyled: 'p',
  'header-one': 'h1',
  'header-two': 'h2',
  'header-three': 'h3',
  'header-four': 'h4',
  'header-five': 'h5',
  'header-six': 'h6',
  'code-block': 'pre',
  blockquote: 'blockquote',
  'unordered-list-item': 'li',
  'ordered-list-item': 'li',
};

const TIPTAP_BLOCK_TO_DRAFT = {
  paragraph: 'unstyled',
  codeBlock: 'code-block',
  blockquote: 'blockquote',
};

const MARK_TO_STYLE = {
  bold: 'BOLD',
  italic: 'ITALIC',
  underline: 'UNDERLINE',
  code: 'CODE',
  strike: 'STRIKETHROUGH',
};

const EMPTY_TIPTAP_HTML = '<p></p>';

// ─── helpers ─────────────────────────────────────────────────────────────────

/**
 * @function genKey
 * @description Generates a short identifier for an exported DraftJS block.
 * @returns {string} The block key.
 */
function genKey() {
  return Math.random().toString(36).substr(2, 5);
}

/**
 * @function escapeHtml
 * @description Escapes stored text and attribute values before interpolating them into generated HTML.
 * @returns {string} HTML-escaped text.
 */
function escapeHtml(str) {
  return String(str || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * @function buildMentionOpenTag
 * @description Maps a legacy mention entity to a typed span with the attributes required by TipTap mention extensions.
 * @returns {string|null} The opening span tag, or null for an unsupported entity type.
 */
function buildMentionOpenTag(entityType, entityData) {
  const nodeType = ENTITY_TYPE_TO_TIPTAP_NODE[entityType];
  if (!nodeType) return null;

  const m = entityData?.mention || entityData || {};
  const pick = (...keys) =>
    keys
      .map((key) => m[key])
      .find((value) => value !== '' && value !== null && value !== undefined) ??
    '';
  const attrs = { 'data-type': nodeType };
  const name = pick('Name', 'name');
  const entityTypeValue = pick('Entity_type', 'entity_type', 'entityType');

  if (nodeType === 'userMention') {
    attrs['data-id'] = pick('id', 'Id', 'user_id', 'userId');
    attrs['data-name'] = name;
    attrs['data-designation'] = m.designation ?? '';
    attrs['data-label'] = name;
    attrs['data-role'] = m.role ?? '';
    attrs['data-user_email'] = m.email ?? m.user_email ?? '';
  } else if (nodeType === 'modelMention' || nodeType === 'artifactMention') {
    const uniqueId = pick('Unique_id', 'unique_id', 'uniqueId');
    attrs['data-id'] = pick('id', 'Id', 'entity_id', 'Entity_id', 'entityId');
    attrs['data-unique-id'] = uniqueId;
    attrs['data-name'] = name;
    attrs['data-entity-type'] = entityTypeValue;
    attrs['data-request-type'] = m.request_type ?? m.requestType ?? '';
    attrs['data-label'] = `${uniqueId} ${name}`.trim();
  } else {
    // documentMention / attributeMention
    const childId = pick('id', 'Id', 'entityId', 'entity_id', 'Entity_id');
    const parentEntityId = pick(
      'parent_entity_id',
      'Parent_entity_id',
      'parentEntityId',
      'Entity_id',
      'entity_id'
    );
    attrs['data-id'] = childId;
    attrs['data-name'] = name;
    attrs['data-entity-id'] = childId;
    attrs['data-entity-type'] = entityTypeValue;
    attrs['data-parent-entity-id'] = parentEntityId;
    attrs['data-label'] = name;
  }

  const attrStr = Object.entries(attrs)
    .filter(([, v]) => v !== '' && v !== null && v !== undefined)
    .map(([k, v]) => `${k}="${escapeHtml(v)}"`)
    .join(' ');

  return `<span ${attrStr}>`;
}

/**
 * @function getEntity
 * @description Resolves a legacy entity using numeric or string keys and handles missing references.
 * @returns {Object|null} The matching DraftJS entity, if present.
 */
function getEntity(entityMap, key) {
  if (key === null || key === undefined) return null;
  return entityMap[key] || entityMap[String(key)] || null;
}

/**
 * @function buildAttrString
 * @description Serializes nonempty HTML attributes with escaped values.
 * @returns {string} The generated HTML attribute string.
 */
function buildAttrString(attrs) {
  return Object.entries(attrs)
    .filter(([, v]) => v !== '' && v !== null && v !== undefined)
    .map(([k, v]) => `${k}="${escapeHtml(v)}"`)
    .join(' ');
}

/**
 * @function renderAtomicImageBlock
 * @description Converts a legacy atomic image entity or block payload into an image tag.
 * @returns {string} Image HTML, or an empty string when no image source exists.
 */
function renderAtomicImageBlock(block, entityMap) {
  const imageRange = (block.entityRanges || []).find((range) => {
    const entity = getEntity(entityMap, range.key);
    return entity?.type === 'IMAGE' && entity?.data?.src;
  });
  const entity = imageRange ? getEntity(entityMap, imageRange.key) : null;
  const imageData = entity?.data || block.data || {};

  if (!imageData?.src) return '';

  return `<img ${buildAttrString({
    src: imageData.src,
    alt: imageData.alt || '',
    title: imageData.title || '',
    width: imageData.width,
    height: imageData.height,
  })}>`;
}

// ─── DraftJS raw → TipTap HTML ───────────────────────────────────────────────

/**
 * @function renderBlockContent
 * @description Converts a DraftJS block's inline ranges to HTML, retaining styles, typed mention spans, and validated hyperlinks.
 * @returns {string} Inline HTML that TipTap can parse.
 */
function renderBlockContent(block, entityMap) {
  if (!block || typeof block !== 'object') return '';
  const { text = '', inlineStyleRanges = [], entityRanges = [] } = block;
  if (!text) return '';

  const n = text.length;

  // Collect boundaries
  const boundaries = new Set([0, n]);
  for (const r of [...inlineStyleRanges, ...entityRanges]) {
    boundaries.add(r.offset);
    boundaries.add(r.offset + r.length);
  }
  const sorted = [...boundaries].sort((a, b) => a - b);

  let html = '';

  for (let bi = 0; bi < sorted.length - 1; bi++) {
    const start = sorted[bi];
    const end = sorted[bi + 1];
    const segment = text.slice(start, end);
    if (!segment) continue;

    // Active styles for this segment
    const activeStyles = inlineStyleRanges
      .filter((r) => r.offset <= start && r.offset + r.length >= end)
      .map((r) => r.style)
      .filter((s) => STYLE_TO_TAG[s]);

    // Active entity for this segment
    const activeEntityRange = entityRanges.find(
      (r) => r.offset <= start && r.offset + r.length >= end
    );

    for (const style of activeStyles) html += `<${STYLE_TO_TAG[style]}>`;

    let entityClosingTag = '';
    if (activeEntityRange) {
      const entity = getEntity(entityMap, activeEntityRange.key);
      if (entity) {
        const href =
          entity.type === 'LINK'
            ? normalizeHyperlink(entity.data?.url || entity.data?.href)
            : null;
        const tag = href
          ? `<a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer">`
          : buildMentionOpenTag(entity.type, entity.data);
        if (tag) {
          html += tag;
          entityClosingTag = href ? '</a>' : '</span>';
        }
      }
    }

    html += escapeHtml(segment);

    html += entityClosingTag;

    for (const style of [...activeStyles].reverse()) {
      html += `</${STYLE_TO_TAG[style]}>`;
    }
  }

  return html;
}

/**
 * @function draftRawToTipTapHtml
 * @description Converts legacy DraftJS content to TipTap-compatible HTML while preserving styles, mentions, images, and validated links.
 * @param {Object|string} raw - DraftJS raw content or its serialized JSON representation.
 * @returns {string} Converted HTML, or an empty paragraph for invalid content.
 */
export function draftRawToTipTapHtml(raw) {
  try {
    const data = typeof raw === 'string' ? JSON.parse(raw) : raw;
    // Malformed historical Draft payloads should not break pages that now
    // render through TipTap. Return a valid empty paragraph instead.
    if (!data || !Array.isArray(data.blocks)) return EMPTY_TIPTAP_HTML;

    const { blocks } = data;
    const entityMap =
      data.entityMap && typeof data.entityMap === 'object'
        ? data.entityMap
        : {};
    const parts = [];
    let i = 0;

    while (i < blocks.length) {
      const block = blocks[i];
      if (!block || typeof block !== 'object') {
        i++;
        continue;
      }
      const type = block.type || 'unstyled';

      if (type === 'atomic') {
        const atomicHtml = renderAtomicImageBlock(block, entityMap);
        if (atomicHtml) parts.push(atomicHtml);
        i++;
        continue;
      }

      if (type === 'unordered-list-item' || type === 'ordered-list-item') {
        const wrapTag = type === 'unordered-list-item' ? 'ul' : 'ol';
        parts.push(`<${wrapTag}>`);
        while (i < blocks.length && blocks[i].type === type) {
          parts.push(`<li>${renderBlockContent(blocks[i], entityMap)}</li>`);
          i++;
        }
        parts.push(`</${wrapTag}>`);
        continue;
      }

      const tag = DRAFT_BLOCK_TO_TAG[type] || 'p';
      parts.push(`<${tag}>${renderBlockContent(block, entityMap)}</${tag}>`);
      i++;
    }

    return parts.join('') || EMPTY_TIPTAP_HTML;
  } catch {
    return EMPTY_TIPTAP_HTML;
  }
}

// ─── TipTap JSON → DraftJS entityMap (backend reference) ────────────────────

/**
 * @function extractEntityMapFromTipTapJson
 * @description Extracts mention entities from TipTap JSON using the legacy entity-map shape expected by backend references.
 * @param {Object|string} content - TipTap document or its serialized JSON representation.
 * @returns {Object} Legacy mention entity map.
 */
export function extractEntityMapFromTipTapJson(content) {
  const entityMap = {};
  let idx = 0;
  try {
    const doc = typeof content === 'string' ? JSON.parse(content) : content;
    if (!doc || doc.type !== 'doc') return entityMap;

    function walk(node) {
      if (!node) return;
      if (TIPTAP_NODE_TO_ENTITY_TYPE[node.type]) {
        const entityType = TIPTAP_NODE_TO_ENTITY_TYPE[node.type];
        const attrs = node.attrs || {};
        let data = {};
        if (node.type === 'userMention') {
          data = {
            mention: {
              id: attrs.id,
              name: attrs.name || attrs.label,
              user: attrs.name,
              user_email: attrs.user_email || '',
              role: attrs.role || '',
              designation: attrs.designation || '',
            },
          };
        } else if (
          node.type === 'modelMention' ||
          node.type === 'artifactMention'
        ) {
          data = {
            mention: {
              Unique_id: attrs.uniqueId,
              Name: attrs.name || attrs.label,
              Entity_type: attrs.entityType,
              request_type: attrs.requestType || '',
              entity_id: attrs.entityId || attrs.id,
            },
          };
        } else {
          // documentMention / attributeMention
          data = {
            mention: {
              Name: attrs.name || attrs.label,
              Entity_id: attrs.parentEntityId,
              Entity_type: attrs.entityType,
              Id: attrs.id,
            },
          };
        }
        entityMap[idx++] = {
          type: entityType,
          mutability: node.type === 'userMention' ? 'SEGMENTED' : 'IMMUTABLE',
          data,
        };
      }
      for (const child of node.content || []) walk(child);
    }
    walk(doc);
  } catch {
    // return whatever was collected
  }
  return entityMap;
}

// ─── TipTap JSON → DraftJS raw ───────────────────────────────────────────────

/**
 * @function getActiveStylesFromMarks
 * @description Maps supported TipTap formatting marks to legacy DraftJS inline-style names.
 * @returns {string[]} Recognized DraftJS style names.
 */
function getActiveStylesFromMarks(marks) {
  if (!marks) return [];
  return marks.map((m) => MARK_TO_STYLE[m.type]).filter(Boolean);
}

/**
 * @function extractInlineContent
 * @description Recursively builds DraftJS text and style/entity ranges from TipTap inline nodes, including mentions and validated links.
 * @returns {Object} Inline text and its corresponding style and entity ranges.
 */
function extractInlineContent(nodes, entityMap, entityIdxRef) {
  let text = '';
  const inlineStyleRanges = [];
  const entityRanges = [];

  function walk(node, inheritedStyles) {
    if (!node) return;

    if (node.type === 'text') {
      const nodeText = node.text || '';
      if (!nodeText) return;
      const start = text.length;
      text += nodeText;

      const nodeStyles = [
        ...new Set([
          ...inheritedStyles,
          ...getActiveStylesFromMarks(node.marks),
        ]),
      ];
      for (const style of nodeStyles) {
        inlineStyleRanges.push({
          style,
          offset: start,
          length: nodeText.length,
        });
      }
      const link = node.marks?.find((mark) => mark.type === 'link');
      const href = link && normalizeHyperlink(link.attrs?.href);
      if (href) {
        const entityKey = entityIdxRef.current;
        // eslint-disable-next-line no-param-reassign
        entityIdxRef.current += 1;
        // eslint-disable-next-line no-param-reassign
        entityMap[entityKey] = {
          type: 'LINK',
          mutability: 'MUTABLE',
          data: { url: href },
        };
        entityRanges.push({
          key: entityKey,
          offset: start,
          length: nodeText.length,
        });
      }
      return;
    }

    if (TIPTAP_NODE_TO_ENTITY_TYPE[node.type]) {
      const entityType = TIPTAP_NODE_TO_ENTITY_TYPE[node.type];
      const attrs = node.attrs || {};
      const label = attrs.label || attrs.name || '';
      const start = text.length;
      text += label;

      let entityData = {};
      if (node.type === 'userMention') {
        entityData = {
          mention: {
            id: attrs.id,
            name: attrs.name || attrs.label,
            designation: attrs.designation || '',
          },
        };
      } else {
        entityData = {
          mention: {
            Unique_id: attrs.uniqueId,
            Name: attrs.name || attrs.label,
            Entity_type: attrs.entityType,
            request_type: attrs.requestType || '',
            entity_id: attrs.entityId || attrs.id,
            entity_type: attrs.entityType,
            parent_entity_id: attrs.parentEntityId,
          },
        };
      }

      // eslint-disable-next-line no-param-reassign
      const eKey = entityIdxRef.current++;
      // eslint-disable-next-line no-param-reassign
      entityMap[eKey] = {
        type: entityType,
        mutability: node.type === 'userMention' ? 'SEGMENTED' : 'IMMUTABLE',
        data: entityData,
      };

      if (label.length > 0) {
        entityRanges.push({ key: eKey, offset: start, length: label.length });
      }
      return;
    }

    if (node.type === 'hardBreak') {
      text += '\n';
      return;
    }

    const childStyles = getActiveStylesFromMarks(node.marks);
    for (const child of node.content || []) {
      walk(child, [...inheritedStyles, ...childStyles]);
    }
  }

  for (const node of nodes || []) {
    walk(node, []);
  }

  return { text, inlineStyleRanges, entityRanges };
}

/**
 * @function getDraftBlockType
 * @description Maps TipTap text blocks and heading levels to their legacy DraftJS block types.
 * @returns {string} The DraftJS block type, defaulting to unstyled.
 */
function getDraftBlockType(node) {
  if (node.type === 'heading') {
    const lvl = {
      1: 'header-one',
      2: 'header-two',
      3: 'header-three',
      4: 'header-four',
      5: 'header-five',
      6: 'header-six',
    };
    return lvl[node.attrs?.level] || 'header-one';
  }
  return TIPTAP_BLOCK_TO_DRAFT[node.type] || 'unstyled';
}

/**
 * @function tiptapJsonToDraftRaw
 * @description Serializes supported TipTap blocks, inline styles, mentions, and links into legacy DraftJS raw content.
 * @param {Object} doc - Document returned by editor.getJSON().
 * @returns {string} Serialized DraftJS raw content with blocks and an entity map.
 */
export function tiptapJsonToDraftRaw(doc) {
  const entityMap = {};
  const entityIdxRef = { current: 0 };
  const blocks = [];

  /**
   * @function processNode
   * @description Appends supported text and list nodes to the exported DraftJS blocks using the shared entity map.
   */
  function processNode(node) {
    if (node.type === 'bulletList' || node.type === 'orderedList') {
      const listType =
        node.type === 'bulletList'
          ? 'unordered-list-item'
          : 'ordered-list-item';
      for (const item of node.content || []) {
        if (item.type === 'listItem') {
          for (const child of item.content || []) {
            const { text, inlineStyleRanges, entityRanges } =
              extractInlineContent(
                child.content || [],
                entityMap,
                entityIdxRef
              );
            blocks.push({
              key: genKey(),
              text,
              type: listType,
              depth: 0,
              inlineStyleRanges,
              entityRanges,
              data: {},
            });
          }
        }
      }
      return;
    }

    if (
      ['paragraph', 'heading', 'codeBlock', 'blockquote'].includes(node.type)
    ) {
      const { text, inlineStyleRanges, entityRanges } = extractInlineContent(
        node.content || [],
        entityMap,
        entityIdxRef
      );
      blocks.push({
        key: genKey(),
        text,
        type: getDraftBlockType(node),
        depth: 0,
        inlineStyleRanges,
        entityRanges,
        data: {},
      });
    }
  }

  for (const node of (doc && doc.content) || []) {
    processNode(node);
  }

  if (blocks.length === 0) {
    blocks.push({
      key: genKey(),
      text: '',
      type: 'unstyled',
      depth: 0,
      inlineStyleRanges: [],
      entityRanges: [],
      data: {},
    });
  }

  return JSON.stringify({ blocks, entityMap });
}
